/**
 * ตะเกียงจุดวาร์ป (ผู้ใช้สั่ง 9 ต.ค. 2026)
 *
 * ทุกโซนเปิด (ไม่ใช่ดันเจี้ยน) มีตะเกียงซ่อนอยู่กลางแผนที่ เข้าแผนที่ใหม่ตะเกียงยังดับ
 * เดินไปหาแล้ว **คลิกที่ตะเกียง** = จุดไฟ = บันทึกเป็นจุดวาร์ป (ไฟติดให้เห็น)
 * วาร์ปไปโซนที่จุดไว้แล้วต้องใช้ไอเทม `lantern_dust` (ซื้อจาก NPC ด้วยโกลด์ — เผาเงินอีกทาง)
 * เมืองหลักแรกจุดไว้ให้เสมอ เมืองอื่นต้องไปจุดเอง · จุดเกิดมีเฉพาะในเมือง (ตะเกียงเป็นแค่จุดวาร์ป) · ดันเจี้ยนไม่มีตะเกียง (ต้องเดินเข้าทางประตูพร้อมปาร์ตี้)
 *
 * server ตัดสินทั้งการจุดและการวาร์ป (ระยะ ของในกระเป๋า สิทธิ์) client ใช้ตัวเดียวกันแสดงผล · pure function
 */
import { MONSTERS, ZONES } from "./data.js";
import { routeNetwork, walkable, wallDistance, zoneHalf } from "./terrain.js";
import { townZoneId } from "./zones.js";
/** ยืนห่างตะเกียงไม่เกินเท่านี้ถึงจะจุดได้ */
export const LANTERN_RANGE = 5;
/** id ไอเทมวาร์ปไปตะเกียง */
export const WAYPOINT_ITEM = "lantern_dust";
export function hasLantern(zone) {
    return !!zone && !zone.isDungeon;
}
const lanternCache = new Map();
/**
 * ตำแหน่งตะเกียง: เมือง = ตะเกียงวิญญาณกลางลาน
 * โซนอื่น = จุดบนทางเดินกลาง ๆ แผนที่ (ห่างจุดเกิด 35–70% ของครึ่งแผนที่) ไม่ใกล้บอสหรือประตู
 * ต้องเดินหาเล็กน้อย (ผู้ใช้สั่ง: ท้าทายนิด ๆ แต่ไม่ยาก) · เลือกจาก seed ของ id โซน ทุกเครื่องได้ที่เดียวกัน
 */
export function lanternPos(zone) {
    if (zone.town)
        return { x: 0, z: 0 };
    const hit = lanternCache.get(zone.id);
    if (hit)
        return hit;
    const H = zoneHalf(zone);
    const sp = zone.spawnPoint;
    const bosses = zone.spawns.filter((s) => MONSTERS[s.monster]?.boss);
    const cands = [];
    for (const [, , x, z] of routeNetwork(zone)) {
        const d = Math.hypot(x - sp.x, z - sp.z);
        if (d < H * 0.35 || d > H * 0.7)
            continue;
        if (bosses.some((b) => Math.hypot(x - b.x, z - b.z) < 25))
            continue;
        if ((zone.portals ?? []).some((p) => Math.hypot(x - p.x, z - p.z) < 15))
            continue;
        if (wallDistance(zone, x, z) > -2)
            continue;
        cands.push({ x: Math.round(x * 10) / 10, z: Math.round(z * 10) / 10 });
    }
    let h = 7;
    for (const ch of zone.id)
        h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    const pos = cands.length ? cands[h % cands.length] : { x: sp.x + 4, z: sp.z + 4 };
    lanternCache.set(zone.id, pos);
    return pos;
}
/** จุดที่โผล่หลังวาร์ป (ข้างตะเกียง ไม่ทับตัวตะเกียง) */
export function lanternArrival(zone) {
    if (zone.town)
        return { x: zone.spawnPoint.x, z: zone.spawnPoint.z };
    const l = lanternPos(zone);
    for (const [dx, dz] of [[2, 0], [-2, 0], [0, 2], [0, -2]]) {
        if (walkable(zone, l.x + dx, l.z + dz))
            return { x: l.x + dx, z: l.z + dz };
    }
    return l;
}
/** อ่านรายการที่เก็บไว้ (คั่น ",") ตัด id ที่ไม่มี/ไม่มีตะเกียงทิ้ง เมืองติดมาเสมอ */
export function parseWaypoints(raw) {
    const list = typeof raw === "string" ? raw.split(",") : [...(raw ?? [])];
    // เมืองหลักแรกจุดไว้ให้เสมอ เมืองอื่นต้องเดินไปถึงก่อนเหมือนแผนที่ทั่วไป
    const out = new Set([townZoneId()]);
    for (const id of list)
        if (hasLantern(ZONES[id]))
            out.add(id);
    return [...out];
}
export function encodeWaypoints(list) {
    return parseWaypoints(list).join(",");
}
/** วาร์ปไปโซนนี้ได้ไหม (ไม่รวมการตรวจไอเทม — server ตรวจเอง) */
export function waypointDenial(unlocked, dest, current) {
    const z = ZONES[dest];
    if (!z)
        return "unknown";
    if (!hasLantern(z))
        return "dungeon";
    if (dest === current)
        return "same-zone";
    if (!unlocked.includes(dest))
        return "not-lit";
    return null;
}
/**
 * เมืองที่เกิดใหม่เมื่อยังไม่เคยบันทึกจุดเกิด: เมืองเลเวลสูงสุดที่ไม่เกินเลเวลของแผนที่นั้น
 * (ตายที่ Lv 100 ไม่ต้องเดินกลับจากเมืองแรก) — จุดเกิดมีเฉพาะในเมืองเท่านั้น
 */
export function nearestTown(zoneId) {
    const lv = ZONES[zoneId]?.levelRange[0] ?? 1;
    let best;
    for (const z of Object.values(ZONES)) {
        if (!z.town || z.levelRange[0] > lv)
            continue;
        if (!best || z.levelRange[0] > best.levelRange[0])
            best = z;
    }
    return best?.id ?? townZoneId();
}
//# sourceMappingURL=waypoints.js.map